/**
 * Hachiman Hero – Slideshow + RGB Glitch
 * File: assets/hachiman-hero.js
 *
 * Depends on GSAP (loaded via CDN in the liquid file).
 * Runs automatically when the DOM is ready.
 */

(function () {

    function init() {
      initSlideshow();
      initGlitchTitles();
      initSmoothScroll();
      initCursorGlow();
      initLightning();
    }
  
    /* ─────────────────────────────────────────
       SLIDESHOW WITH GSAP RGB GLITCH
    ───────────────────────────────────────── */
    function initSlideshow() {
      // Support multiple hero sections on same page (shouldn't happen but safe)
      document.querySelectorAll('.hm-hero').forEach(function (hero) {
        var slides    = Array.from(hero.querySelectorAll('.hm-hero-slide'));
        var dotsCont  = hero.querySelector('.hm-slide-dots');
        var slideshow = hero.querySelector('.hm-hero-slideshow');
  
        if (!slides.length || !slideshow) return;
  
        // Captions (name / class / price) under the PC, one per slide, same order.
        var caps     = Array.from(hero.querySelectorAll('.hm-slide-cap'));
        // With a caption to read, each PC holds longer than the bare glitch reel.
        var INTERVAL = caps.length ? 4500 : 2800;
        var current  = 0;
        var timer    = null;
  
        // ── Init first slide ──
        slides.forEach(function (s, i) {
          s.style.opacity    = i === 0 ? '1' : '0';
          s.style.visibility = i === 0 ? 'visible' : 'hidden';
          if (i === 0) s.classList.add('active');
        });
  
        // ── Build dot nav ──
        if (dotsCont) {
          slides.forEach(function (_, i) {
            var d = document.createElement('button');
            d.className = 'hm-slide-dot' + (i === 0 ? ' active' : '');
            d.setAttribute('aria-label', 'Show ' + ((caps[i] && caps[i].querySelector('.hm-cap-name')) ? caps[i].querySelector('.hm-cap-name').textContent : 'PC ' + (i + 1)));
            // A picked PC gets a full interval on screen before the reel moves on.
            d.addEventListener('click', function () { goTo(i); restart(); });
            dotsCont.appendChild(d);
          });
        }
  
        function updateDots(idx) {
          caps.forEach(function (c, i) {
            var on = i === idx;
            c.classList.toggle('active', on);
            if (c.tagName === 'A') {
              if (on) { c.removeAttribute('tabindex'); c.removeAttribute('aria-hidden'); }
              else { c.setAttribute('tabindex', '-1'); c.setAttribute('aria-hidden', 'true'); }
            }
          });
          if (!dotsCont) return;
          dotsCont.querySelectorAll('.hm-slide-dot').forEach(function (d, i) {
            d.classList.toggle('active', i === idx);
            d.setAttribute('aria-current', i === idx ? 'true' : 'false');
          });
        }
  
        // ── Create RGB split layers ──
        var redL  = document.createElement('img');
        var cyanL = document.createElement('img');
        redL.className  = 'hm-rgb-red';
        cyanL.className = 'hm-rgb-cyan';
        // Decorative chromatic-aberration duplicates of the active slide —
        // hide from assistive tech (the real slide carries the alt text).
        redL.alt = cyanL.alt = '';
        redL.setAttribute('aria-hidden', 'true');
        cyanL.setAttribute('aria-hidden', 'true');
        slideshow.appendChild(redL);
        slideshow.appendChild(cyanL);

  
        // ── Transition function ──
        function goTo(nextIdx) {
          if (nextIdx === current || typeof gsap === 'undefined') return;
  
          var cur  = slides[current];
          var next = slides[nextIdx];

          redL.src = cyanL.src = cur.src;

          // Only the RGB overlay layers move during the glitch — center them in
          // GSAP's model so their x offsets are relative to center (not the CSS
          // translate, which GSAP would misread as pixels and fling to a corner).
          // The main photo's transform is left entirely to CSS (it just opacity-
          // crossfades below), so it can never shift on any viewport.
          gsap.set([redL, cyanL], { xPercent: -50, yPercent: -50 });

          // Glitch OUT current slide
          var tlOut = gsap.timeline();
          tlOut
            .to([redL, cyanL], { opacity: 0.65, duration: 0.015 })
            .to(redL,  { x: -14, duration: 0.03 }, '<')
            .to(cyanL, { x:  14, duration: 0.03 }, '<')
            .to(redL,  { x:  18, y: -4, duration: 0.025 })
            .to(cyanL, { x: -18, y:  4, duration: 0.025 }, '<')
            .to(redL,  { x: -10, y:  2, duration: 0.025 })
            .to(cyanL, { x:  10, y: -2, duration: 0.025 }, '<')
            .to([redL, cyanL], { opacity: 0, x: 0, y: 0, duration: 0.06 })
            .to(cur, {
              opacity: 0,
              duration: 0.18,
              ease: 'power2.in',
              onComplete: function () {
                cur.classList.remove('active');
                cur.style.visibility = 'hidden';
              }
            });

          // Glitch IN next slide
          next.style.visibility = 'visible';
          var tlIn = gsap.timeline({ delay: 0.22 });
          tlIn
            .set([redL, cyanL], { opacity: 0 })
            .call(function () { redL.src = cyanL.src = next.src; })
            .fromTo(next,
              { opacity: 0 },
              { opacity: 1, duration: 0.12, ease: 'power2.out' }
            )
            .to([redL, cyanL], { opacity: 0.55, duration: 0.015 })
            .to(redL,  { x:  16, duration: 0.025 }, '<')
            .to(cyanL, { x: -16, duration: 0.025 }, '<')
            .to(redL,  { x: -10, duration: 0.025 })
            .to(cyanL, { x:  10, duration: 0.025 }, '<')
            .to([redL, cyanL], { opacity: 0, x: 0, duration: 0.05 })
            .to(next, {
              duration: 0.03,
              onStart: function () { next.classList.add('active'); },
              onComplete: function () { gsap.set(next, { clearProps: 'transform' }); }
            }, '<');
  
          current = nextIdx;
          updateDots(current);
        }
  
        // ── Auto-rotate ── (paused while the pointer is on the caption, so it
        // doesn't change under someone about to click it)
        var hold = false;
        function restart() {
          clearInterval(timer);
          timer = setInterval(function () {
            if (!hold) goTo((current + 1) % slides.length);
          }, INTERVAL);
        }
        var meta = hero.querySelector('.hm-slide-meta');
        if (meta) {
          meta.addEventListener('mouseenter', function () { hold = true; });
          meta.addEventListener('mouseleave', function () { hold = false; });
          meta.addEventListener('focusin',  function () { hold = true; });
          meta.addEventListener('focusout', function () { hold = false; });
        }
        restart();
      });
    }
  
    /* ─────────────────────────────────────────
       GLITCH TEXT ON CARD TITLE HOVER
    ───────────────────────────────────────── */
    function initGlitchTitles() {
      document.querySelectorAll('.hm-card-title').forEach(function (el) {
        el.addEventListener('mouseenter', function () {
          var orig  = this.textContent;
          var chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
          var iter  = 0;
          var self  = this;
          var iv = setInterval(function () {
            self.textContent = orig.split('').map(function (c, j) {
              if (c === ' ') return ' ';
              return j < iter ? orig[j] : chars[Math.floor(Math.random() * chars.length)];
            }).join('');
            iter += 0.5;
            if (iter >= orig.length) {
              clearInterval(iv);
              self.textContent = orig;
            }
          }, 40);
        });
      });
    }
  
    /* ─────────────────────────────────────────
       SMOOTH SCROLL FOR ANCHOR LINKS
    ───────────────────────────────────────── */
    function initSmoothScroll() {
      document.querySelectorAll('a[href^="#"]').forEach(function (a) {
        a.addEventListener('click', function (e) {
          var href = this.getAttribute('href');
          if (href && href.length > 1) {
            var target = document.querySelector(href);
            if (target) {
              e.preventDefault();
              target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        });
      });
    }
  
    /* ─────────────────────────────────────────
       CUSTOM CURSOR — GUN SCOPE + LOCK-ON + SHOCKWAVE
    ───────────────────────────────────────── */
    function initCursorGlow() {
      var isTouch = window.matchMedia('(pointer: coarse)').matches;

      document.querySelectorAll('.hm-hero').forEach(function (hero) {
        var cursor = hero.querySelector('.hm-custom-cursor');
        var canvas = hero.querySelector('.hm-trail-canvas');
        if (!cursor || !canvas) return;

        cursor.style.display = 'none'; /* scope is fully canvas-drawn */

        var ctx        = canvas.getContext('2d');
        var spotlight  = hero.querySelector('.hm-hero-spotlight-overlay');
        var LOCK_MS    = 280;
        var TAU        = Math.PI * 2;
        var SCOPE_R    = 36;
        var INNER_R    = 7;
        var SPOT_R     = 220;  /* spotlight radius in px */

        function resizeCanvas() {
          canvas.width  = hero.offsetWidth;
          canvas.height = hero.offsetHeight;
        }
        resizeCanvas();
        new ResizeObserver(resizeCanvas).observe(hero);

        /* Cache the hero's rect — it only changes on scroll/resize, not on every
           mousemove/frame. Reading it in the hot paths forced a layout reflow. */
        var heroRectCache = hero.getBoundingClientRect();
        function refreshHeroRect() { heroRectCache = hero.getBoundingClientRect(); }
        window.addEventListener('scroll', refreshHeroRect, { passive: true });
        window.addEventListener('resize', refreshHeroRect, { passive: true });

        /* ── State ── */
        var mouse          = { x: -999, y: -999 };
        var cur            = { x: -999, y: -999 };
        var curOpacity     = 0;
        var inside         = false, isHovering = false;
        var lockEl         = null, lockT = 0, lockFlash = 0;
        var shockwaves     = [];
        var beamFire       = 0;
        var breathPhase    = 0;
        var shurikenAngle  = 0;
        var touchFadeTimer = null;

        /* ── Helpers ── */
        function lerp(a, b, t)   { return a + (b - a) * t; }
        function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

        function setPos(clientX, clientY) {
          var rect = heroRectCache;
          mouse.x = clientX - rect.left;
          mouse.y = clientY - rect.top;
          if (cur.x < -900) { cur.x = mouse.x; cur.y = mouse.y; }
          inside = true;
        }

        /* ── Mouse events ── */
        if (!isTouch) {
          hero.addEventListener('mousemove', function (e) {
            setPos(e.clientX, e.clientY);
            if (spotlight) {
              var rect = heroRectCache;
              var x = e.clientX - rect.left, y = e.clientY - rect.top;
              spotlight.style.background =
                'radial-gradient(circle ' + SPOT_R + 'px at ' + x + 'px ' + y + 'px, ' +
                'transparent 0%, transparent 25%, rgba(0,0,0,0.70) 75%)';
            }
          });
          hero.addEventListener('mouseleave', function () {
            inside = false;
            if (spotlight) { spotlight.style.background = 'rgba(0,0,0,0.70)'; }
          });

          /* ── Lock-on ── */
          hero.querySelectorAll('a, button').forEach(function (el) {
            el.addEventListener('mouseenter', function () {
              isHovering = true; lockEl = el; lockT = Date.now(); lockFlash = 0;
            });
            el.addEventListener('mouseleave', function () {
              isHovering = false; lockEl = null;
            });
          });

          /* ── Click: fire + shockwave ── */
          hero.addEventListener('mousedown', function () {
            beamFire = Date.now();
            shockwaves.push({ x: cur.x, y: cur.y, t: Date.now() });
          });
        }

        /* ── Touch events ── */
        if (isTouch) {
          hero.addEventListener('touchstart', function (e) {
            if (touchFadeTimer) { clearTimeout(touchFadeTimer); touchFadeTimer = null; }
            var t = e.touches[0];
            setPos(t.clientX, t.clientY);
            beamFire = Date.now();
            shockwaves.push({ x: mouse.x, y: mouse.y, t: Date.now() });
          }, { passive: true });

          hero.addEventListener('touchmove', function (e) {
            var t = e.touches[0];
            setPos(t.clientX, t.clientY);
          }, { passive: true });

          hero.addEventListener('touchend', function () {
            touchFadeTimer = setTimeout(function () { inside = false; }, 600);
          }, { passive: true });

          hero.addEventListener('touchcancel', function () {
            inside = false;
          }, { passive: true });
        }

        /* ── Draw: shuriken + targeting ring ── */
        function drawScope(opacity) {
          if (opacity < 0.01) return;

          var now  = Date.now();
          var fire = beamFire > 0 ? Math.max(0, 1 - (now - beamFire) / 300) : 0;
          var sx   = cur.x, sy = cur.y;
          var R    = 20;
          var RING = R + 20;
          var red  = 'rgba(198,32,38,';

          ctx.save();
          ctx.globalAlpha = opacity;

          /* outer glow halo */
          var glow = ctx.createRadialGradient(sx, sy, R * 0.4, sx, sy, RING);
          glow.addColorStop(0, red + (0.10 + fire * 0.18) + ')');
          glow.addColorStop(1, 'rgba(198,32,38,0)');
          ctx.beginPath(); ctx.arc(sx, sy, RING, 0, TAU);
          ctx.fillStyle = glow; ctx.fill();

          /* shuriken blades */
          ctx.save();
          ctx.translate(sx, sy);
          ctx.rotate(shurikenAngle);
          for (var i = 0; i < 4; i++) {
            ctx.save();
            ctx.rotate(i * Math.PI / 2);
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(-3.5, 3.5);
            ctx.lineTo(0, -R);
            ctx.lineTo(3.5, 3.5);
            ctx.closePath();
            ctx.fillStyle   = red + (0.88 + fire * 0.12) + ')';
            ctx.shadowColor = '#c62026';
            ctx.shadowBlur  = 8 + fire * 10;
            ctx.fill();
            ctx.strokeStyle = 'rgba(255,160,160,' + (0.3 + fire * 0.5) + ')';
            ctx.lineWidth   = 0.5;
            ctx.stroke();
            ctx.restore();
          }
          /* center rivet */
          ctx.beginPath(); ctx.arc(0, 0, 4, 0, TAU);
          ctx.fillStyle   = '#c62026';
          ctx.shadowColor = '#c62026';
          ctx.shadowBlur  = 12;
          ctx.fill();
          ctx.beginPath(); ctx.arc(0, 0, 6.5, 0, TAU);
          ctx.strokeStyle = red + '0.5)';
          ctx.lineWidth   = 1;
          ctx.shadowBlur  = 0;
          ctx.stroke();
          ctx.restore();

          /* dashed targeting ring (static, doesn't rotate) */
          ctx.globalAlpha = opacity * (0.30 + 0.08 * Math.sin(breathPhase * 2));
          ctx.beginPath(); ctx.arc(sx, sy, RING, 0, TAU);
          ctx.strokeStyle = red + '0.75)';
          ctx.lineWidth   = 0.75;
          ctx.setLineDash([4, 6]);
          ctx.shadowColor = '#c62026';
          ctx.shadowBlur  = 4;
          ctx.stroke();
          ctx.setLineDash([]);

          /* NSEW pips */
          ctx.globalAlpha = opacity;
          ctx.strokeStyle = red + '0.9)';
          ctx.lineWidth   = 1.5;
          ctx.lineCap     = 'square';
          ctx.shadowBlur  = 6;
          [[0,-1],[1,0],[0,1],[-1,0]].forEach(function (n) {
            ctx.beginPath();
            ctx.moveTo(sx + n[0] * (RING - 1), sy + n[1] * (RING - 1));
            ctx.lineTo(sx + n[0] * (RING + 8),  sy + n[1] * (RING + 8));
            ctx.stroke();
          });

          /* fire flash */
          if (fire > 0) {
            ctx.globalAlpha = opacity * fire * 0.35;
            ctx.beginPath(); ctx.arc(sx, sy, R + 6, 0, TAU);
            ctx.fillStyle = 'rgba(255,200,200,1)'; ctx.fill();
          }

          ctx.restore();
        }

        /* ── Draw: lock-on brackets ── */
        function drawBrackets() {
          if (!lockEl) return;
          var now = Date.now(), heroRect = heroRectCache, elRect = lockEl.getBoundingClientRect();
          var progress = Math.min((now - lockT) / LOCK_MS, 1), eased = easeOutCubic(progress);
          if (progress >= 1 && lockFlash === 0) lockFlash = now;
          var PAD = 10, ARM = 14, SPREAD = 26 * (1 - eased);
          var bx = (elRect.left - heroRect.left) - PAD - SPREAD, by = (elRect.top - heroRect.top) - PAD - SPREAD;
          var bw = elRect.width + PAD * 2 + SPREAD * 2, bh = elRect.height + PAD * 2 + SPREAD * 2;
          if (lockFlash > 0) {
            var fa = (1 - (now - lockFlash) / 220) * 0.7;
            if (fa > 0) { ctx.strokeStyle = 'rgba(220,110,115,' + fa + ')'; ctx.lineWidth = 3; ctx.strokeRect(bx, by, bw, bh); }
          }
          ctx.strokeStyle = 'rgba(198,32,38,' + (0.45 + 0.55 * eased) + ')'; ctx.lineWidth = 1.5; ctx.lineCap = 'square';
          ctx.beginPath(); ctx.moveTo(bx, by + ARM);           ctx.lineTo(bx, by);            ctx.lineTo(bx + ARM, by);            ctx.stroke();
          ctx.beginPath(); ctx.moveTo(bx + bw - ARM, by);      ctx.lineTo(bx + bw, by);       ctx.lineTo(bx + bw, by + ARM);       ctx.stroke();
          ctx.beginPath(); ctx.moveTo(bx, by + bh - ARM);      ctx.lineTo(bx, by + bh);       ctx.lineTo(bx + ARM, by + bh);       ctx.stroke();
          ctx.beginPath(); ctx.moveTo(bx + bw - ARM, by + bh); ctx.lineTo(bx + bw, by + bh); ctx.lineTo(bx + bw, by + bh - ARM);  ctx.stroke();
          if (progress >= 1) {
            ctx.strokeStyle = 'rgba(198,32,38,' + (0.12 + 0.09 * Math.sin(now * 0.007)) + ')'; ctx.lineWidth = 0.5; ctx.setLineDash([4, 4]);
            ctx.strokeRect(bx + ARM, by + ARM, bw - ARM * 2, bh - ARM * 2); ctx.setLineDash([]);
            var mx = bx + bw / 2, my = by + bh / 2, PIP = 4; ctx.strokeStyle = 'rgba(198,32,38,0.65)'; ctx.lineWidth = 1.5;
            ctx.beginPath(); ctx.moveTo(mx - PIP, by);      ctx.lineTo(mx + PIP, by);      ctx.stroke();
            ctx.beginPath(); ctx.moveTo(mx - PIP, by + bh); ctx.lineTo(mx + PIP, by + bh); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(bx, my - PIP);      ctx.lineTo(bx, my + PIP);      ctx.stroke();
            ctx.beginPath(); ctx.moveTo(bx + bw, my - PIP); ctx.lineTo(bx + bw, my + PIP); ctx.stroke();
          }
        }

        /* ── Draw: shockwaves ── */
        function drawShockwaves() {
          var now = Date.now();
          shockwaves = shockwaves.filter(function (s) { return now - s.t < 700; });
          shockwaves.forEach(function (s) {
            var age = (now - s.t) / 700, r = age * 110, a = 1 - age;
            ctx.beginPath(); ctx.arc(s.x, s.y, r, 0, TAU); ctx.strokeStyle = 'rgba(198,32,38,' + (a * 0.22) + ')'; ctx.lineWidth = 10; ctx.stroke();
            ctx.beginPath(); ctx.arc(s.x, s.y, r * 0.97, 0, TAU); ctx.strokeStyle = 'rgba(198,32,38,' + (a * 0.9) + ')'; ctx.lineWidth = 2; ctx.stroke();
            if (age > 0.1) {
              ctx.beginPath(); ctx.arc(s.x, s.y, (age - 0.1) * 110, 0, TAU);
              ctx.strokeStyle = 'rgba(255,255,255,' + ((1 - age) * 0.55) + ')'; ctx.lineWidth = 1; ctx.stroke();
            }
          });
        }

        /* ── rAF loop ── */
        function tick() {
          breathPhase += 0.008;
          var now = Date.now();
          var fire = beamFire > 0 ? Math.max(0, 1 - (now - beamFire) / 300) : 0;
          breathPhase   += 0.025;
          shurikenAngle += isHovering ? 0.07 : fire > 0 ? 0.18 : 0.012;
          cur.x      = mouse.x;
          cur.y      = mouse.y;
          curOpacity = lerp(curOpacity, inside ? 1 : 0, 0.30);

          ctx.clearRect(0, 0, canvas.width, canvas.height);
          if (!isTouch) drawScope(curOpacity);
          drawShockwaves();
          drawBrackets();

          requestAnimationFrame(tick);
        }

        tick();
      });
    }

    /* ─────────────────────────────────────────
       LIGHTNING FLASH EFFECT
    ───────────────────────────────────────── */
    function initLightning() {
      document.querySelectorAll('.hm-hero').forEach(function (hero) {
        var el = hero.querySelector('.hm-hero-lightning');
        if (!el) return;

        function flash(steps, idx) {
          if (idx >= steps.length) { schedule(); return; }
          var s = steps[idx];
          el.style.transition = s.fade ? 'opacity ' + s.fade + 'ms ease' : 'none';
          el.style.opacity    = s.opacity;
          setTimeout(function () { flash(steps, idx + 1); }, s.hold);
        }

        function strike() {
          flash([
            { opacity: 0.25, hold: 40,  fade: 0  }, /* main strike */
            { opacity: 0.10, hold: 20,  fade: 0  }, /* dim */
            { opacity: 0,    hold: 0,   fade: 80 }  /* quick fade */
          ], 0);
        }

        function schedule() {
          setTimeout(strike, 3500 + Math.random() * 5500);
        }

        setTimeout(strike, 3000);
      });
    }

    /* ─────────────────────────────────────────
       WAIT FOR GSAP THEN INIT
    ───────────────────────────────────────── */
    function waitForGsap(cb, attempts) {
      attempts = attempts || 0;
      if (typeof gsap !== 'undefined') {
        cb();
      } else if (attempts < 30) {
        setTimeout(function () { waitForGsap(cb, attempts + 1); }, 100);
      }
    }
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { waitForGsap(init); });
    } else {
      waitForGsap(init);
    }
  
  })();